import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Input, Space, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { AxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Highlighter from 'react-highlight-words';

import { convertMoedaBRDoJSNovo, poeValorComPontosEmMil } from '../../utils/Conversores';
import { formatCpfCnpj, onlyNumbers } from '../../utils/Masks';
import { EmitError } from '../../utils/EmitError';
import { findChunksCpfCnpj, findChunksText, HIGHLIGHT_STYLE } from '../../utils/Highlight';
import InvoicesService, { ICompanyWithStats } from '../../services/http/services/InvoicesService';
import useAuth from '../../hooks/useAuth';
import { useTableParams } from '../../hooks/useTableParams';
import { IActiveFilterItem } from '../../components/ActiveFiltersBar';
import TableActionButton from '../../components/TableActionButton';
import { ThemeContext } from '../../contexts/ThemeContext';

export interface ITableData extends ICompanyWithStats {
    rowKey: number | string;
    value: number;
    totalInvoice: {
        all: number;
        completed: number;
        warning: number;
        error: number;
    };
}

export type TypeSortCompany = 'company' | 'value' | 'totalInvoice';

export interface ICityCompanyInvoicesFilters {
    company: string | null;
    cpfCnpj: string | null;
}

function getSortOrder(columnKey: string, sorter?: { field: string; order: 'asc' | 'desc' }): SortOrder | undefined {
    if (!sorter || sorter.field !== columnKey) return undefined;
    return sorter.order === 'asc' ? 'ascend' : 'descend';
}

export default function useCityCompanyInvoices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalCompanies, setTotalCompanies] = useState<number>(0);

    const companyInputRef = useRef<any>(null);
    const cpfCnpjInputRef = useRef<any>(null);

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
        lastSorterRef,
    } = useTableParams<ICityCompanyInvoicesFilters, TypeSortCompany>({
        storageKey: '@NotaMunicipio:invoicesCompanyByCity',
        defaultFilters: {
            company: null,
            cpfCnpj: null,
        },
        defaultSort: {
            field: 'totalInvoice',
            order: 'desc',
        },
        defaultPageSize: 50,
    });

    const [searchCompany, setSearchCompany] = useState(tableParams.filters.company || '');
    const [searchCpfCnpj, setSearchCpfCnpj] = useState(tableParams.filters.cpfCnpj || '');

    const [searchParams] = useSearchParams();

    useEffect(() => {
        const companyParam = searchParams.get('company');
        const cpfCnpjParam = searchParams.get('cpfCnpj');

        if (companyParam || cpfCnpjParam) {
            handleCustomChange({
                newPage: 1,
                newFilters: {
                    ...(companyParam ? { company: companyParam } : {}),
                    ...(cpfCnpjParam ? { cpfCnpj: cpfCnpjParam } : {}),
                },
            });
            if (companyParam) setSearchCompany(companyParam);
            if (cpfCnpjParam) setSearchCpfCnpj(cpfCnpjParam);
        }
    }, [searchParams, handleCustomChange]);

    useEffect(() => {
        setSearchCompany(tableParams.filters.company || '');
    }, [tableParams.filters.company]);

    useEffect(() => {
        setSearchCpfCnpj(tableParams.filters.cpfCnpj || '');
    }, [tableParams.filters.cpfCnpj]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.company
            ? {
                key: 'company',
                label: 'Empresa',
                value: tableParams.filters.company,
                onClear: () => {
                    setSearchCompany('');
                    handleFilterClear('company');
                },
            }
            : null,
        tableParams.filters.cpfCnpj
            ? {
                key: 'cpfCnpj',
                label: 'CPF/CNPJ',
                value: formatCpfCnpj(tableParams.filters.cpfCnpj),
                onClear: () => {
                    setSearchCpfCnpj('');
                    handleFilterClear('cpfCnpj');
                },
            }
            : null,
    ];

    const columns: ColumnsType<ITableData> = [
        {
            title: 'Empresa',
            dataIndex: 'company',
            key: 'company',
            sorter: true,
            sortOrder: getSortOrder('company', tableParams.sorter),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar por Empresa
                    </div>
                    <Input
                        ref={companyInputRef}
                        placeholder="Nome do estabelecimento..."
                        value={searchCompany}
                        onChange={(e) => setSearchCompany(e.target.value)}
                        onPressEnter={() => {
                            confirm();
                            handleCustomChange({
                                newPage: 1,
                                newFilters: { company: searchCompany.trim() || null },
                            });
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { company: searchCompany.trim() || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchCompany('');
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { company: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: tableParams.filters.company ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => companyInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13 }}>
                    {tableParams.filters.company ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[tableParams.filters.company]}
                            findChunks={findChunksText}
                            autoEscape
                            textToHighlight={record.company || '--'}
                        />
                    ) : (
                        record.company || '--'
                    )}
                </span>
            ),
            width: 280,
            ellipsis: true,
        },
        {
            title: 'CPF/CNPJ',
            dataIndex: 'cpfCnpj',
            key: 'cpfCnpj',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar por CPF/CNPJ
                    </div>
                    <Input
                        ref={cpfCnpjInputRef}
                        placeholder="Digite o CPF ou CNPJ..."
                        value={formatCpfCnpj(searchCpfCnpj)}
                        onChange={(e) => setSearchCpfCnpj(formatCpfCnpj(e.target.value))}
                        onPressEnter={() => {
                            confirm();
                            handleCustomChange({
                                newPage: 1,
                                newFilters: { cpfCnpj: searchCpfCnpj.trim() || null },
                            });
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { cpfCnpj: searchCpfCnpj.trim() || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchCpfCnpj('');
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { cpfCnpj: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: tableParams.filters.cpfCnpj ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => cpfCnpjInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => {
                const formatted = record.cpfCnpj ? formatCpfCnpj(String(record.cpfCnpj)) : '--';
                return (
                    <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {tableParams.filters.cpfCnpj ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[tableParams.filters.cpfCnpj]}
                                findChunks={findChunksCpfCnpj}
                                autoEscape
                                textToHighlight={formatted}
                            />
                        ) : (
                            formatted
                        )}
                    </span>
                );
            },
            width: 180,
        },
        {
            title: (
                <Tooltip title="Notas: Válidas / Alerta / Erro / Total">
                    <span style={{ cursor: 'help' }}>Notas</span>
                </Tooltip>
            ),
            dataIndex: 'totalInvoice',
            key: 'totalInvoice',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('totalInvoice', tableParams.sorter),
            showSorterTooltip: false,
            render: (value?: any) => {
                const all = value?.all ?? 0;
                const completed = value?.completed ?? 0;
                const warning = value?.warning ?? 0;
                const error = value?.error ?? 0;

                return (
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 13.5, fontWeight: 600 }}>
                        <Tooltip color="#4cae4f" title="Notas válidas">
                            <span style={{ color: isDark ? '#49aa19' : '#4cae4f', cursor: 'pointer' }}>
                                {poeValorComPontosEmMil(completed)}
                            </span>
                        </Tooltip>
                        <span style={{ color: isDark ? '#595959' : '#94a3b8', fontWeight: 400 }}>/</span>
                        <Tooltip color="#F8B019" title="Notas com alerta">
                            <span style={{ color: isDark ? '#d48806' : '#F8B019', cursor: 'pointer' }}>
                                {poeValorComPontosEmMil(warning)}
                            </span>
                        </Tooltip>
                        <span style={{ color: isDark ? '#595959' : '#94a3b8', fontWeight: 400 }}>/</span>
                        <Tooltip color="#FC4C4E" title="Notas com erro">
                            <span style={{ color: isDark ? '#cf1322' : '#FC4C4E', cursor: 'pointer' }}>
                                {poeValorComPontosEmMil(error)}
                            </span>
                        </Tooltip>
                        <span style={{ color: isDark ? '#595959' : '#94a3b8', fontWeight: 400 }}>/</span>
                        <Tooltip color="#1875bb" title="Total de notas">
                            <span style={{ color: isDark ? '#177ddc' : '#1875bb', cursor: 'pointer' }}>
                                {poeValorComPontosEmMil(all)}
                            </span>
                        </Tooltip>
                    </div>
                );
            },
            width: 200,
        },
        {
            title: 'Valor Total',
            dataIndex: 'value',
            key: 'value',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('value', tableParams.sorter),
            showSorterTooltip: false,
            render: (value?: number) => (
                <Tooltip color="#389e0d" title="Soma dos valores das notas fiscais válidas">
                    <span style={{ fontWeight: 700, color: isDark ? '#52c41a' : '#237804', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value ?? 0)}
                    </span>
                </Tooltip>
            ),
            width: 160,
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 90,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TableActionButton
                        action="view"
                        tooltip="Ver todas as notas deste estabelecimento"
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            localStorage.removeItem('@NotaMunicipio:allInvoices');
                            navigate('/notas/geral', { state: { company: record.company } });
                        }}
                    />
                </div>
            ),
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<ICompanyWithStats>) => {
        const newData = data
            .filter((d) => d.company !== null)
            .map((invoice, index) => ({
                ...invoice,
                rowKey: `${invoice.company}-${invoice.cpfCnpj}-${index}`,
                value: invoice.total_value,
                totalInvoice: {
                    all: invoice.total_invoice,
                    completed: invoice.completed_invoices,
                    error: invoice.error_invoices,
                    warning: invoice.warning_invoices,
                },
            }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function fetchCompanies() {
            try {
                setIsLoading(true);
                const response = await InvoicesService.listAllCompanyByCity({
                    cityId: activeCity?.id!,
                    page: tableParams.pagination.current,
                    size: tableParams.pagination.pageSize,
                    sort: (tableParams.sorter.field as TypeSortCompany) ?? 'totalInvoice',
                    order: (tableParams.sorter.order as 'asc' | 'desc') ?? 'desc',
                    company: tableParams.filters.company || undefined,
                    cpfCnpj: onlyNumbers(tableParams.filters.cpfCnpj) || undefined,
                    signal: controller.signal,
                });

                const companiesFormatted = response.companies.map((company) => ({
                    ...company,
                    cpfCnpj: company.company ? company.cpfCnpj : 0,
                }));

                setTableDataFromRequest(companiesFormatted);
                setTotalCompanies(response.total);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'cityCompanyInvoices');
                    setIsLoading(false);
                }
            }
        }

        fetchCompanies();

        return () => controller.abort();
    }, [activeCity, tableParams, setTableDataFromRequest]);

    return {
        columns,
        isLoading,
        tableData,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        tableParams,
        totalCompanies,
        activeFilterItems,
        lastSorterRef,
    };
}

